import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  acceptanceTerms,
  policies,
  type EndorsementDetail,
  type PolicyDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { RichTextView } from '@/shared/ui/RichTextEditor';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';

import { regrasDoErroDoEndosso, regrasEmTexto } from './endossoFinanceiro';
import { EndorsementFinancialPaymentData } from './EndorsementFinancialPayment';

/**
 * Passo "Informacoes de pagamento" (END-010; END-119 a END-123 no que o endosso
 * neutro precisa).
 *
 * O endosso neutro nao gera premio: as informacoes de pagamento nao sao necessarias. O
 * "Termo e declaracao" e o termo de aceite vigente do tipo endosso (P-USU-2), com a
 * caixa "Declaro que li e estou ciente das condicoes acima.", gravada como declaracao.
 *
 * "Emitir endosso" e a emissao da operacao do tipo endosso, que so acontece quando a
 * esteira chega a emissao e para quem emite (`emissao.apolice.create`). No fluxo de
 * solicitacao, a decisao e em /endossos.
 *
 * P-END-2: no endosso de alteracao e no de cancelamento, os dados financeiros e as
 * parcelas da cotacao de endosso e a comissao ligada; a emissao responde com as regras
 * pendentes do premio e da restituicao, e a tela mostra quais.
 */
export function EndorsementPaymentStep({
  endorsement,
  policy = null,
  onVoltar,
}: {
  endorsement: EndorsementDetail;
  policy?: PolicyDetail | null;
  onVoltar: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [declarado, setDeclarado] = useState(false);

  const termo = useQuery({
    queryKey: ['acceptance-term', 'endosso'],
    queryFn: ({ signal }) => acceptanceTerms.current('endosso', signal),
    retry: false,
  });

  const operacao = endorsement.operation ?? null;
  const naEmissao =
    operacao?.stage === 'em_emissao' && endorsement.status === 'em_analise';
  const podeEmitir = can('emissao.apolice.create');
  const temTermo = termo.isSuccess && termo.data !== null;

  const emitir = useMutation({
    mutationFn: async () => {
      if (!operacao) throw new Error('Erro ao emitir o endosso');
      if (temTermo && termo.data) {
        await acceptanceTerms.approve(termo.data.id, {
          endorsement_id: endorsement.id,
        });
      }
      return policies.issue(operacao.id, {});
    },
    onSuccess: () => {
      toast.success('Solicitacao de endosso enviada para seguradora.');
      void queryClient.invalidateQueries({ queryKey: ['endorsement', endorsement.id] });
      void queryClient.invalidateQueries({
        queryKey: ['policy', endorsement.policy_id],
      });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
    onError: (error: Error) => toast.error(error.message || 'Erro ao emitir o endosso'),
  });
  const financeiro = endorsement.financial ?? null;
  const regrasDaEmissao = regrasDoErroDoEndosso(emitir.error);

  const declarar = useMutation({
    mutationFn: () => {
      if (!termo.data) throw new Error('Erro ao buscar termos de aceite');
      return acceptanceTerms.approve(termo.data.id, { endorsement_id: endorsement.id });
    },
    onSuccess: () => toast.success('Declaracao registrada.'),
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="flex flex-col gap-4">
      {financeiro ? (
        <EndorsementFinancialPaymentData endorsement={endorsement} policy={policy} />
      ) : (
        <Card>
          <CardHeader title="Dados financeiros" />
          <CardBody className="flex flex-col gap-2">
            {endorsement.calculation_pending ? (
              <Alert tone="warning" title="Regra pendente">
                Premio, comissao, forma de pagamento e parcelas do endosso dependem de
                calculo ainda nao homologado (P-END-2).
              </Alert>
            ) : (
              <>
                <p className="text-sm text-ink-900">
                  As informacoes de pagamento nao serao necessarias desta vez.
                </p>
                <p className="text-sm text-ink-600">
                  Cotacao de endosso nao gerou premio a cobrar, prossiga com a emissao.
                </p>
              </>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Termo e declaracao"
          description="Leia os termos para solicitacao de endosso"
        />
        <CardBody className="flex flex-col gap-3">
          {termo.isError ? (
            <Alert tone="danger" title="Erro ao buscar termos de aceite">
              {termo.error.message}
            </Alert>
          ) : null}
          {termo.isSuccess && termo.data === null ? (
            <p className="text-sm text-ink-600">
              A empresa ainda nao tem termo de aceite de endosso vigente.
            </p>
          ) : null}
          {temTermo && termo.data ? (
            <>
              <p className="text-sm font-medium text-ink-900">{termo.data.title}</p>
              <div className="max-h-64 overflow-y-auto rounded-md border border-line p-3">
                <RichTextView html={termo.data.body} />
              </div>
              <label className="flex items-center gap-2 text-sm text-ink-900">
                <input
                  type="checkbox"
                  checked={declarado}
                  onChange={(event) => setDeclarado(event.target.checked)}
                  className="size-4 rounded border-line-strong text-brand-500"
                />
                Declaro que li e estou ciente das condicoes acima.
              </label>
            </>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Emissao do endosso" />
        <CardBody className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Situacao do endosso:</span>
            <StatusBadge kind="situacaoEndosso" value={endorsement.status} />
          </div>
          {endorsement.status === 'rascunho' ? (
            <p className="text-sm text-ink-600">
              Gere a cotacao no resumo da alteracao antes de seguir para a emissao.
            </p>
          ) : null}
          {endorsement.status === 'solicitado' ? (
            <p className="text-sm text-ink-600">
              O endosso aguarda a decisao em Endossos: a empresa usa o fluxo de
              solicitacao.
            </p>
          ) : null}
          {operacao ? (
            <p className="text-sm text-ink-600">
              Operacao {operacao.number} na etapa{' '}
              {statusLabel('operacao', operacao.stage)}.{' '}
              <Link
                to={`/operacoes/${operacao.id}?aba=follow-up`}
                className="text-brand-600 underline-offset-2 hover:underline"
              >
                Ir para timeline
              </Link>
            </p>
          ) : null}
          {endorsement.status === 'em_analise' && !naEmissao ? (
            <p className="text-sm text-ink-600">
              A cotacao de endosso segue a esteira; a emissao abre quando ela chegar a
              etapa de emissao.
            </p>
          ) : null}
          {naEmissao && !podeEmitir ? (
            <p className="text-sm text-ink-600">
              Aguardando a emissao pela seguradora.
            </p>
          ) : null}
          {financeiro &&
          endorsement.status !== 'emitido' &&
          endorsement.status !== 'aprovado' ? (
            <Alert tone="warning" title="A emissao espera a regra">
              {financeiro.issuance_rules.message} Regras pendentes:{' '}
              {regrasEmTexto(financeiro.issuance_rules)}.
            </Alert>
          ) : null}
          {emitir.isError && regrasDaEmissao.length > 0 ? (
            <Alert tone="danger" title="Erro ao emitir o endosso">
              {emitir.error.message} Regras pendentes: {regrasDaEmissao.join(', ')}.
            </Alert>
          ) : null}
          {endorsement.status === 'emitido' || endorsement.status === 'aprovado' ? (
            <Alert tone="success" title="Endosso aplicado na apolice">
              A fotografia das partes da apolice foi trocada pelo endosso, e o movimento
              esta na aba Historico da apolice.
            </Alert>
          ) : null}
        </CardBody>
      </Card>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onVoltar}>
          Voltar
        </Button>
        {temTermo && endorsement.status !== 'rascunho' && !naEmissao ? (
          <Button
            variant="secondary"
            loading={declarar.isPending}
            disabled={!declarado}
            onClick={() => declarar.mutate()}
          >
            Salvar endosso
          </Button>
        ) : null}
        {naEmissao && podeEmitir ? (
          <Button
            loading={emitir.isPending}
            disabled={temTermo && !declarado}
            onClick={() => emitir.mutate()}
          >
            Emitir endosso
          </Button>
        ) : null}
      </div>
    </div>
  );
}
